/**
 * PlayerEntity — shared base class for Minna and Basil.
 *
 * Encapsulates the common logic: capsule collider creation, glTF model loading,
 * physics-based movement, ground detection, model flipping, jumping, and disposal.
 * Subclasses only need to add character-specific visuals and behavior.
 *
 * Ground detection uses a physics raycast from the capsule feet (pre-allocated
 * vectors to avoid per-frame allocations).
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import "@babylonjs/loaders/glTF";
import { PLAYER, PHYSICS } from "../../config/GameConfig";
import { applyJumpCut, shapeFall, resolveMovementProfile, MovementOverrides } from "../../systems/JumpFallModel";
import { InputSource } from "../../systems/InputSource";
import { JumpWindowTracker } from "../../systems/JumpWindowTracker";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../../levels/PlaceholderTag";
import { FootstepCadence } from "../../systems/audio/FootstepCadence";
import { qaRulesActive } from "../../qa/qaRules";
import { CatAnimator, type CatClip, type CatClipInfo } from "./CatAnimator";

/** Ground probe: rays start this far above the feet (inside the capsule)… */
const GROUND_PROBE_START = 0.05;
/** …and reach this far below them. */
const GROUND_PROBE_REACH = 0.09;
/** Centre, then either side (× 0.7 capsule radius). */
const GROUND_PROBE_OFFSETS = [0, -1, 1] as const;
/** Upward speed above which a cat is never grounded (just jumped). */
const GROUND_MAX_RISE_SPEED = 0.1;
/**
 * Collision group for the two cats. They collide with the world but not
 * with each other (as in Unravel Two): in solo mode the following cat used
 * to body-block the active one, and one cat could end up perched on the
 * other's head, unable to jump. No puzzle relies on stacking.
 */
export const CAT_COLLISION_GROUP = 1 << 1;
const ALL_GROUPS = 0xffffffff;
const NOT_CATS = (ALL_GROUPS & ~CAT_COLLISION_GROUP) >>> 0;
/** Solo-follow ledge guard: how far ahead of the capsule to probe… */
const COMPANION_LOOKAHEAD = 0.25;
/** …and the largest drop a following cat will walk down on its own. */
const COMPANION_MAX_STEP_DOWN = 0.8;
/** A step-to-step move larger than this is a teleport: not interpolated. */
const INTERP_SNAP_DISTANCE = 1.5;
/** Ground rays ignore cats, so overlapping your partner isn't "ground". */
const GROUND_QUERY = { membership: ALL_GROUPS, collideWith: NOT_CATS };

export interface PlayerConfig {
  /** Unique name for mesh/light identification (e.g. "minna", "basil"). */
  name: string;
  /** Path to the glTF model (e.g. "/assets/models/minna.glb"). */
  modelPath: string;
  /** Optional uniform scale applied to the imported model root. */
  modelScale?: number;
  /** Optional local offset applied after floor alignment. */
  modelOffset?: Vector3;
  /** Optional Y rotation override for the imported model root. */
  modelRotationY?: number;
  /** Physics body mass. */
  mass: number;
  /** Capsule collider radius. */
  capsuleRadius: number;
  /** Capsule collider height. */
  capsuleHeight: number;
  /** Air acceleration multiplier (1.0 = normal, <1 = heavier feel). */
  airAccelerationScale: number;
  /** Point light diffuse color. */
  lightColor: Color3;
  /** Point light specular color. */
  lightSpecular: Color3;
  /** Point light intensity. */
  lightIntensity: number;
  /** Point light range. */
  lightRange: number;
  /** Point light local offset from the collider mesh. */
  lightOffset: Vector3;
  /**
   * Optional base tint applied to the character's fur/body material after
   * the glTF loads. Some source GLBs ship with a pure-white `TabbyFur` /
   * `Body` material that has no albedo texture, which reads as a ghostly
   * white blob under cool lighting. Supplying a tint here lets each
   * character enforce a character-appropriate base color at runtime.
   *
   * The tint matches any cloned material whose name matches `/fur|body/i`
   * and applies only if that material has no albedoTexture.
   */
  furTint?: Color3;
  /**
   * Distance (world units) the character must travel on the ground before
   * a PLAYER_FOOTSTEP event fires. Minna uses 0.6 (light, quick steps);
   * Basil uses 0.9 (heavier, longer stride).
   */
  footstepStrideDistance: number;
  /**
   * Durations/strides for the skeletal clips (see catAnimClips.ts),
   * strides already in world units (× modelScale). Only used when the model
   * ships the full CatAnimator clip set; durations default to the clip's own.
   */
  animClips?: Partial<Record<CatClip, CatClipInfo>>;

  // ── Per-cat movement overrides (Task 8 tuning) ──────────────────────────
  // All optional. Omitting any field falls back to the shared PLAYER.* default,
  // so Minna and Basil are byte-identical to the pre-profile baseline until
  // Task 8 supplies values.
  /** Override PLAYER.MOVE_SPEED for this character. */
  moveSpeed?: number;
  /** Override PLAYER.JUMP_IMPULSE for this character. */
  jumpImpulse?: number;
  /** Override PLAYER.ACCELERATION for this character. */
  acceleration?: number;
  /** Override PLAYER.DECELERATION for this character. */
  deceleration?: number;
  /**
   * Override PLAYER.AIR_ACCELERATION for this character (the raw value,
   * BEFORE multiplying by airAccelerationScale — that composition is
   * preserved inside applyInput as before).
   */
  airAcceleration?: number;
  /** Override PLAYER.AIR_DECELERATION for this character. */
  airDeceleration?: number;
  /** Override PLAYER.MAX_FALL_SPEED for this character. */
  maxFallSpeed?: number;
}

export class PlayerEntity {
  public mesh: Mesh;
  public aggregate: PhysicsAggregate;
  protected characterLight: PointLight;
  protected isGrounded: boolean = false;
  /** Public read-only grounded-state for QA / NavigationSystem introspection. */
  public get grounded(): boolean {
    return this.isGrounded;
  }
  protected modelMeshes: AbstractMesh[] = [];
  protected modelRoot: TransformNode | null = null;
  protected modelInstance: InstantiatedModel | null = null;
  /** Skeletal animation driver; null → procedural pose only. */
  protected animator: CatAnimator | null = null;
  protected config: PlayerConfig;
  /**
   * Public read-only accessor for the cat's PlayerConfig.
   * Used by NavigationSystem (Task 7) to build the per-cat movement
   * profile for NavigatorDeps without importing the full entity class.
   */
  public get playerConfig(): PlayerConfig {
    return this.config;
  }
  protected scene: Scene;
  /** Movement speed multiplier (1.0 = normal). Set by EnergySystem for depletion penalty. */
  public moveSpeedScale: number = 1.0;

  /**
   * Set true when the character is pinned by an ability or state
   * machine and should ignore movement input (e.g. Pneumatic Anchor).
   * When true and the player still tries to walk, `applyInput` triggers
   * a brief mesh rattle so the player gets a visible "you're stuck
   * here" cue instead of silently eating the input.
   */
  public movementBlocked: boolean = false;
  private deniedInputRattle = 0;

  /**
   * When true (default), fast-fall input is honoured. Set to false by
   * NavigationSystem while it owns the cat so the navigator's constant-
   * gravity arc solver does not undershot due to an early fast-fall.
   * Task 4 gates fast-fall behaviour on this flag.
   */
  public fastFallEnabled = true;

  /**
   * Companion walk override. When non-zero, applyInput uses this value
   * as `moveAxis` instead of reading from the InputSource — the
   * primitive a future autonomous-partner-AI will write to so the
   * inactive cat can react to the shared-camera edge by walking
   * toward the active cat instead of being teleported. -1 = walk
   * west, +1 = walk east, 0 = no override (read input normally).
   *
   * Only honored when `movementBlocked` is false — pinned cats stay
   * pinned even if a companion-walk request comes in.
   *
   * Currently unwritten by any system; reserved for the partner-AI
   * feature.
   */
  public companionWalk: -1 | 0 | 1 = 0;

  /** Pre-allocated vectors for ground raycast to avoid per-frame allocations. */
  private _rayStart = new Vector3();
  private _rayEnd = new Vector3();
  private jumpWindow = new JumpWindowTracker(PLAYER.COYOTE_TIME, PLAYER.JUMP_BUFFER_TIME);
  private modelBasePosition: Vector3 | null = null;
  private modelBaseScale = 1;
  private modelBaseRotationY = Math.PI / 2;
  /** Visual-only facing sign (+1 east / -1 west). Protected so ability
   *  pose overlays (Q-032 Basil DJ-scratch) can mirror with the model. */
  protected facingDirection = 1;
  private locomotionPhase = 0;
  /** Smoothed 0..1 locomotion amount. Protected so ability pose overlays
   *  can damp themselves while the cat is walking. Visual-only. */
  protected smoothedMoveAmount = 0;
  private landingImpact = 0;

  /**
   * Post-jump "launch compress" — a brief 1 → 0 ramp that fires when
   * `jumpedThisFrame` is true and decays over ~140ms. While it's active
   * we override the standard jump stretch with a squat-to-stretch
   * transition so Basil/Minna read as actually pushing off the ground.
   * Addresses a Gemini fluidity review note: "jump transition lacks
   * anticipatory frames — pose snaps from idle to airborne."
   */
  private launchCompress = 0;

  /**
   * Procedural limb rig. The glTF ships as 18 (Minna) / 25 (Basil)
   * separate meshes — no armature, no skinned weights — so we animate
   * limbs by reaching into the model hierarchy and offsetting each
   * part's transform per-frame. The cat walk cycle uses a classic
   * diagonal gait: front-left + back-right move together, 180°
   * out of phase with front-right + back-left.
   */
  private limbLegFL: TransformNode | null = null;
  private limbLegFR: TransformNode | null = null;
  private limbLegBL: TransformNode | null = null;
  private limbLegBR: TransformNode | null = null;
  private limbTail: TransformNode | null = null;
  private limbHead: TransformNode | null = null;
  private limbEarL: TransformNode | null = null;
  private limbEarR: TransformNode | null = null;
  private limbBasePositions = new Map<TransformNode, Vector3>();
  private limbBaseRotations = new Map<TransformNode, Vector3>();
  private earTwitchTimer = 0;
  private earTwitchOffset = 0;

  /** Distance-accumulator that fires PLAYER_FOOTSTEP once per stride. */
  private cadence: FootstepCadence;

  constructor(scene: Scene, spawnPosition: Vector3, config: PlayerConfig) {
    this.scene = scene;
    this.config = config;
    this.cadence = new FootstepCadence(config.footstepStrideDistance);

    // Invisible capsule for physics collisions
    this.mesh = MeshBuilder.CreateCapsule(
      `${config.name}_collider`,
      {
        height: config.capsuleHeight,
        radius: config.capsuleRadius,
        tessellation: 16,
        subdivisions: 6,
      },
      scene
    );
    this.mesh.position = spawnPosition.clone();
    this.mesh.isVisible = false; // Hidden -- glTF model is the visual
    // Tag the capsule collider as a placeholder. If the glTF model loads
    // successfully in `loadModel` it will be tagged "final" so audits see
    // both: the invisible collider (placeholder) and the visible art (final).
    tagAs(this.mesh, "placeholder");

    // Character-specific point light
    this.characterLight = new PointLight(`${config.name}Light`, Vector3.Zero(), scene);
    this.characterLight.diffuse = config.lightColor;
    this.characterLight.specular = config.lightSpecular;
    this.characterLight.intensity = config.lightIntensity;
    this.characterLight.range = config.lightRange;
    this.characterLight.parent = this.mesh;
    this.characterLight.position = config.lightOffset.clone();

    // Physics capsule
    this.aggregate = new PhysicsAggregate(
      this.mesh,
      PhysicsShapeType.CAPSULE,
      // Zero friction: horizontal motion is set directly every frame, so
      // friction only ever acted against walls — pinning a cat that pressed
      // into a platform's side in mid-air ("wall-stick", unable to jump).
      { mass: config.mass, friction: 0, restitution: 0.05 },
      scene
    );
    const shape = this.aggregate.shape as PhysicsAggregate["shape"] | undefined;
    if (shape) { // absent in physics-less unit-test stubs
      shape.filterMembershipMask = CAT_COLLISION_GROUP;
      shape.filterCollideMask = NOT_CATS;
    }
    const body = this.aggregate.body;
    body.setAngularDamping(1000);
    // Passing only `inertia` to setMassProperties causes Havok to recompute
    // `mass` from shape volume × default density (~450 for a cat-sized
    // capsule), overriding the explicit mass set on the aggregate. Pass
    // both so the configured mass is preserved.
    body.setMassProperties({ mass: config.mass, inertia: Vector3.Zero() });

    // Load glTF model and parent to the collider
    this.loadModel(scene);
  }

  private async loadModel(scene: Scene): Promise<void> {
    try {
      // Route through AssetManager so repeated PlayerEntity instances (and
      // level restarts that reuse the scene) hit the cached template.
      const instance = await getAssetManager().instantiateModel(scene, this.config.modelPath);
      this.modelInstance = instance;
      this.modelMeshes = instance.meshes;

      // Parent root to collider and offset so model is centered on capsule.
      // instantiateModelsToScene returns TransformNode roots (glTF __root__).
      const root = instance.rootNodes[0];
      if (!root) {
        throw new Error(`[${this.config.name}] model has no root node`);
      }
      this.modelRoot = root;
      root.parent = this.mesh;
      this.modelBaseScale = this.config.modelScale ?? 1;
      root.scaling.setAll(this.modelBaseScale);
      // Rotate to face along Y+ (model faces Y+, camera looks from Z)
      this.modelBaseRotationY = this.config.modelRotationY ?? Math.PI / 2;
      root.rotation = new Vector3(0, this.modelBaseRotationY, 0);

      // Align the model's visual bottom with the capsule collider bottom
      // so paws actually touch the surface the physics says they're on.
      // A hardcoded `-capsuleHeight * 0.4` was leaving the cats floating
      // ~0.4 units above the platform because the glTF root origin is
      // above the paws. Compute the real offset from the mesh hierarchy.
      const modelOffset = this.config.modelOffset ?? Vector3.Zero();
      root.position = new Vector3(modelOffset.x, 0, modelOffset.z);
      root.computeWorldMatrix(true);
      for (const mesh of instance.meshes) {
        mesh.computeWorldMatrix(true);
      }
      const bbVectors = root.getHierarchyBoundingVectors(true);
      const targetBottom = this.mesh.position.y - this.config.capsuleHeight / 2;
      root.position.y = targetBottom - bbVectors.min.y + modelOffset.y;
      this.modelBasePosition = root.position.clone();

      // Tag the loaded character art as production. The capsule collider
      // remains tagged "placeholder" but is invisible at runtime.
      tagAs(root, "final");
      for (const mesh of instance.meshes) {
        tagAs(mesh, "final");
      }

      // Apply furTint fallback so characters don't render as pure white
      // when the source GLB lacks an albedo texture on fur/body materials.
      if (this.config.furTint) {
        this.applyFurTint(instance.meshes, this.config.furTint);
      }

      // Cache limb references for procedural animation. The glTF ships
      // as flat meshes named `<Name>_Leg_FL` etc. Both Minna and Basil
      // use the same naming so this works for both.
      this.cacheLimbReferences(instance.meshes);
      this.animator = CatAnimator.create(instance.animationGroups ?? [], this.config.animClips);
      this.onModelLoaded(instance);
    } catch (e) {
      console.warn("[%s] Failed to load glTF model, using collider as fallback:", this.config.name, e);
      this.mesh.isVisible = true; // Show capsule as fallback
    }
  }

  /**
   * Tint fur/body materials that ship without an albedo texture. The source
   * GLBs use pure-white PBR albedo on fur materials and rely on a (missing)
   * texture for character colors; without a texture they read as a ghostly
   * white blob. This fallback only touches materials that look untextured
   * and white so we don't override correctly authored assets.
   */
  private applyFurTint(meshes: AbstractMesh[], tint: Color3): void {
    for (const mesh of meshes) {
      const mat = mesh.material as
        | { name?: string; albedoColor?: Color3; albedoTexture?: unknown }
        | null;
      if (!mat || !mat.name || !mat.albedoColor) continue;
      if (!/fur|body|head|ear|snout|tail|leg|paw/i.test(mat.name)) continue;
      if (mat.albedoTexture) continue; // Respect authored textures.
      const c = mat.albedoColor;
      const isWhite = c.r > 0.95 && c.g > 0.95 && c.b > 0.95;
      if (!isWhite) continue;
      mat.albedoColor = tint.clone();
    }
  }

  /**
   * Subclass hook invoked once the glTF instance is parented, floor-aligned,
   * tagged, and tinted. Default no-op. Runs only on successful load — the
   * capsule-fallback path never calls it.
   */
  protected onModelLoaded(_instance: InstantiatedModel): void {}

  /** Collider position after the previous / latest fixed physics step. */
  private _prevStepPos = new Vector3();
  private _currStepPos = new Vector3();
  private _hasStepPos = false;
  /** Model-root local position computed by the last procedural pose. */
  private _poseRootPos = new Vector3();

  /** Call after every fixed physics step (see BaseLevel's lockstep loop). */
  recordStepPosition(): void {
    const p = this.mesh.position;
    if (!this._hasStepPos || Vector3.DistanceSquared(p, this._currStepPos) > INTERP_SNAP_DISTANCE ** 2) {
      // First step or a teleport/respawn: don't smear the jump.
      this._prevStepPos.copyFrom(p);
    } else {
      this._prevStepPos.copyFrom(this._currStepPos);
    }
    this._currStepPos.copyFrom(p);
    this._hasStepPos = true;
  }

  /**
   * Draw the model between the last two physics steps. `alpha` is how far
   * the renderer is into the next step (0..1). The collider itself stays
   * at the simulated position; only the visual child is offset.
   */
  interpolateVisual(alpha: number): void {
    const root = this.modelRoot;
    if (!root || !this._hasStepPos || !this.modelBasePosition) return;
    const back = 1 - alpha;
    root.position.set(
      this._poseRootPos.x + (this._prevStepPos.x - this._currStepPos.x) * back,
      this._poseRootPos.y + (this._prevStepPos.y - this._currStepPos.y) * back,
      this._poseRootPos.z,
    );
  }

  /**
   * Solo-follow ledge guard: true when a following (idle) cat, standing on
   * a surface, would walk off an edge with no ground within
   * COMPANION_MAX_STEP_DOWN below. It then waits at the edge instead —
   * playtest found idle Minna following Basil off her starting sill onto a
   * floor she needed precision jumps to leave. The catch-up warp in
   * SoloFollow still brings a stranded cat along.
   */
  private companionLedgeAhead(dir: -1 | 1): boolean {
    if (!this.isGrounded) return false;
    const physicsEngine = this.scene.getPhysicsEngine();
    if (!physicsEngine) return false;
    const feetY = this.mesh.position.y - this.config.capsuleHeight * 0.5;
    const aheadX = this.mesh.position.x + dir * (this.config.capsuleRadius + COMPANION_LOOKAHEAD);
    this._rayStart.set(aheadX, feetY + GROUND_PROBE_START, this.mesh.position.z);
    this._rayEnd.set(aheadX, feetY - COMPANION_MAX_STEP_DOWN, this.mesh.position.z);
    return !physicsEngine.raycast(this._rayStart, this._rayEnd, GROUND_QUERY).hasHit;
  }

  applyInput(input: InputSource, deltaTime: number): void {
    const body = this.aggregate.body;
    const velocity = body.getLinearVelocity();
    const wasGrounded = this.isGrounded;

    // Resolve per-cat movement profile once per call.
    // When no overrides are set (Minna/Basil untuned), every value equals
    // the corresponding PLAYER.* constant — zero behaviour change.
    const overrides: MovementOverrides = {
      moveSpeed:       this.config.moveSpeed,
      jumpImpulse:     this.config.jumpImpulse,
      acceleration:    this.config.acceleration,
      deceleration:    this.config.deceleration,
      airAcceleration: this.config.airAcceleration,
      airDeceleration: this.config.airDeceleration,
      maxFallSpeed:    this.config.maxFallSpeed,
    };
    const mp = resolveMovementProfile(overrides, PLAYER);

    let targetVx = 0;
    const effectiveSpeed = mp.moveSpeed * this.moveSpeedScale;
    // companionWalk overrides input-driven moveX so the inactive cat
    // can react to a shared-camera edge by walking toward its partner.
    // Only when not movement-blocked — a pinned cat stays pinned.
    const moveAxis =
      this.companionWalk !== 0 && !this.movementBlocked
        ? (this.companionLedgeAhead(this.companionWalk) ? 0 : this.companionWalk)
        : input.getAxis("moveX");
    if (moveAxis < -0.01) targetVx -= effectiveSpeed;
    if (moveAxis > 0.01) targetVx += effectiveSpeed;

    // Movement-blocked feedback. If an ability (currently only
    // Pneumatic Anchor) has pinned this cat, intercept any horizontal
    // move attempt, zero it out, and flash a brief rattle so the
    // player gets visible feedback that the input was recognised but
    // refused — addresses a Gemini 3/3-run agreement that anchored
    // Basil eats 'a'/'d' inputs with zero signal.
    if (this.movementBlocked) {
      if (Math.abs(moveAxis) > 0.01) {
        this.deniedInputRattle = 1;
      }
      targetVx = 0;
    }

    // Detect QA surface: when active, skip player-feel modifiers so the
    // navigator sees the canonical pre-Phase-3 jump arc (running_jump recipes
    // hold the jump key only ~80ms then release mid-ascent; without this gate
    // the release-only jump-cut fires and the cat undershoots).
    // Also gates per-cat accel/decel so the navigator's stopping-distance model
    // (tuned to PLAYER defaults) is unaffected by Minna's higher deceleration
    // (50 vs default 35) — fixes L3-shadowRelay navigate timeout regression.
    // jumpImpulse / moveSpeed / maxFall / air values stay ungated:
    //   - jumpImpulse: navigator's arc solver already threads per-cat values via
    //     NavigatorDeps (Task 7); gating it would break Basil's arc predictions.
    //   - moveSpeed: unchanged at 5 for both cats (no drift to fix).
    //   - maxFall / air-accel: not involved in ground stopping distance.
    const qaActive = qaRulesActive();

    // airAccelerationScale composition is preserved exactly:
    // mp.airAcceleration is the raw default (PLAYER.AIR_ACCELERATION when
    // untuned), and config.airAccelerationScale applies on top as before.
    // Under QA, ground accel/decel fall back to PLAYER defaults so the
    // navigator's stopping-distance model sees the expected ramp.
    const groundAccel = qaActive ? PLAYER.ACCELERATION : mp.acceleration;
    const groundDecel = qaActive ? PLAYER.DECELERATION : mp.deceleration;
    const accel = this.isGrounded
      ? groundAccel
      : mp.airAcceleration * this.config.airAccelerationScale;
    const currentVx = velocity.x;
    let newVx: number;

    if (targetVx !== 0) {
      const diff = targetVx - currentVx;
      const maxChange = accel * deltaTime;
      newVx = Math.abs(diff) < maxChange
        ? targetVx
        : currentVx + Math.sign(diff) * maxChange;
    } else {
      const decel = this.isGrounded ? groundDecel : mp.airDeceleration;
      const maxChange = decel * deltaTime;
      newVx = Math.abs(currentVx) < maxChange
        ? 0
        : currentVx - Math.sign(currentVx) * maxChange;
    }

    let vy = velocity.y;
    // 1. Variable jump height: cut upward velocity on early button release.
    //    Skipped under QA so the navigator gets full canonical jump arcs.
    if (!qaActive) {
      vy = applyJumpCut(vy, input.justReleased("jump"), PLAYER.JUMP_CUT_MULTIPLIER);
    }
    // 2. Fast-fall: extra downward acceleration when descending (navigator disables via fastFallEnabled=false).
    //    Also skipped under QA (blanket gate) so navigator arc is unmodified.
    if (this.fastFallEnabled && !qaActive) {
      const fallAccel = Math.abs(PHYSICS.GRAVITY_Y) * (PLAYER.FALL_MULTIPLIER - 1);
      vy = shapeFall(vy, deltaTime, fallAccel, mp.maxFallSpeed);
    }
    // 3. Terminal velocity clamp — shapeFall already clamps internally; this guard
    //    ensures no upstream path (e.g. physics tunnelling) exceeds the cap.
    //    Must come AFTER fast-fall so the grounded settle check (Math.abs(vy) < 0.05)
    //    below sees the modified vy, not a pre-fast-fall value.
    if (vy < -mp.maxFallSpeed) vy = -mp.maxFallSpeed;

    body.setLinearVelocity(new Vector3(newVx, vy, 0));

    // Keep Z at 0
    if (Math.abs(this.mesh.position.z) > 0.01) this.mesh.position.z = 0;

    // Ground detection: short downward rays from just above the feet at the
    // capsule's centre and near both sides (so a cat resting on a ledge
    // edge still counts). `capsuleHeight` is the capsule's TOTAL height, so
    // the feet are at centre.y - capsuleHeight/2. The previous probe
    // subtracted the radius as well, started inside the floor, never hit
    // while standing, and fell back to `|vy| < 0.05` — which is also true
    // at a jump's apex, so Minna could re-jump there and climb forever
    // (docs/qa/PLAYTEST-HARNESS-FINDINGS-2026-09-26.md). Rays that start
    // inside the cat's own capsule ignore it.
    const physicsEngine = this.scene.getPhysicsEngine();
    if (physicsEngine) {
      const feetY = this.mesh.position.y - this.config.capsuleHeight * 0.5;
      const side = this.config.capsuleRadius * 0.7;
      let hit = false;
      for (const dx of GROUND_PROBE_OFFSETS) {
        this._rayStart.set(this.mesh.position.x + dx * side, feetY + GROUND_PROBE_START, this.mesh.position.z);
        this._rayEnd.set(this._rayStart.x, feetY - GROUND_PROBE_REACH, this._rayStart.z);
        if (physicsEngine.raycast(this._rayStart, this._rayEnd, GROUND_QUERY).hasHit) { hit = true; break; }
      }
      // Not while moving upward: the rays still see the floor for a frame
      // or two after take-off, which used to re-arm coyote time and let a
      // quick second press stack another jump impulse.
      this.isGrounded = hit && vy < GROUND_MAX_RISE_SPEED;
    }

    const landedThisFrame = this.isGrounded && !wasGrounded;
    if (landedThisFrame) {
      gameEvents.emit(GameEvents.PLAYER_LANDED, this.config.name);
    }

    // Footstep cadence — emit once per stride of grounded horizontal travel.
    // landedThisFrame is excluded so the landing thud and first footstep
    // don't collide on the same frame.
    if (!landedThisFrame && this.cadence.update(Math.abs(newVx), deltaTime, this.isGrounded)) {
      gameEvents.emit(GameEvents.PLAYER_FOOTSTEP, { cat: this.config.name as 'minna' | 'basil' });
    }

    // Jump
    const jumpedThisFrame = this.jumpWindow.update(deltaTime, this.isGrounded, input.justPressed("jump"));
    if (jumpedThisFrame) {
      body.setLinearVelocity(new Vector3(newVx, mp.jumpImpulse, 0));
      this.isGrounded = false;
      this.launchCompress = 1;
      gameEvents.emit(GameEvents.PLAYER_JUMPED, this.config.name);
    }

    this.updateProceduralPose(deltaTime, moveAxis, newVx, jumpedThisFrame ? mp.jumpImpulse : vy, landedThisFrame, jumpedThisFrame);
  }

  /**
   * Shared Camera edge restriction. Per 2026-04-14 companion book canon
   * (Section III, "True Simultaneous Co-Op"), the frame itself is the
   * instructor: straying too far from the partner causes the edges of
   * the screen to gently restrict movement.
   *
   * Implementation: if this cat is beyond `MAX_SEPARATION` on the X
   * axis, scale any OUTWARD-moving X velocity by a damping factor that
   * ramps from 1 at MAX_SEPARATION to 0 at MAX_SEPARATION + SOFT_BAND.
   * Inward movement (toward the partner) is never damped — the player
   * can always close the gap at full speed. Call once per frame per
   * cat AFTER applyInput but BEFORE physics stepping.
   */
  clampToSeparation(otherX: number, maxSep = PLAYER.MAX_SEPARATION): void {
    const body = this.aggregate.body;
    const velocity = body.getLinearVelocity();
    const dx = this.mesh.position.x - otherX;
    const absDx = Math.abs(dx);
    if (absDx <= maxSep) return;

    // Ramp from 1.0 at maxSep to 0.0 at maxSep + BAND
    const overflow = absDx - maxSep;
    const damping = Math.max(0, 1 - overflow / PLAYER.SEPARATION_SOFT_BAND);

    // Only damp the component of velocity moving AWAY from the partner.
    // dx > 0 means this cat is to the right of the partner; outward =
    // further positive velocity. dx < 0 means inverse.
    const isMovingOutward =
      (dx > 0 && velocity.x > 0) || (dx < 0 && velocity.x < 0);
    if (!isMovingOutward) return;

    const newVx = velocity.x * damping;
    body.setLinearVelocity(new Vector3(newVx, velocity.y, 0));

    // Telemetry: emit a throttled event whenever damping is actually
    // applied. QA traces consume this signal to diagnose "navigator
    // walks but cat barely moves" bugs — almost always the camera
    // tether. Throttled to at most 10 Hz so a persistent clamp
    // doesn't flood the event-history ring buffer.
    const now =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    if (now - PlayerEntity._lastClampEmitAt > 100) {
      PlayerEntity._lastClampEmitAt = now;
      gameEvents.emit(GameEvents.PLAYER_SEPARATION_CLAMPED, {
        cat: this.config.name as "minna" | "basil",
        separation: absDx,
        damping,
        selfX: this.mesh.position.x,
        partnerX: otherX,
      });
    }
  }

  private static _lastClampEmitAt = 0;

  dispose(): void {
    this.characterLight.dispose();
    this.aggregate.dispose();
    this.animator?.dispose();
    this.animator = null;
    // Dispose the instantiated clone (template in AssetManager cache is kept).
    if (this.modelInstance) {
      this.modelInstance.dispose();
      this.modelInstance = null;
    }
    this.modelMeshes = [];
    this.modelRoot = null;
    this.mesh.dispose();
  }

  private cacheLimbReferences(meshes: AbstractMesh[]): void {
    // Both Minna and Basil use `<Character>_<Part>` naming. We match on
    // the suffix because the glTF roots may prefix with "Clone of" or
    // similar when instantiated from a cached template.
    const matchSuffix = (suffix: RegExp): AbstractMesh | undefined =>
      meshes.find((m) => suffix.test(m.name));

    const record = (limb: AbstractMesh | undefined): TransformNode | null => {
      if (!limb) return null;
      const node = limb as unknown as TransformNode;
      // Snapshot base transform so we animate *around* whatever pose the
      // glTF ships with.
      this.limbBasePositions.set(node, node.position.clone());
      this.limbBaseRotations.set(node, node.rotation.clone());
      return node;
    };

    this.limbLegFL = record(matchSuffix(/Leg_FL$/));
    this.limbLegFR = record(matchSuffix(/Leg_FR$/));
    this.limbLegBL = record(matchSuffix(/Leg_BL$/));
    this.limbLegBR = record(matchSuffix(/Leg_BR$/));
    this.limbTail  = record(matchSuffix(/Tail$/));
    this.limbHead  = record(matchSuffix(/_Head$/));
    this.limbEarL  = record(matchSuffix(/Ear_L$/));
    this.limbEarR  = record(matchSuffix(/Ear_R$/));
  }

  /**
   * Apply the walk-cycle / idle pose to every cached limb. This is
   * called from `updateProceduralPose` after the root-level squash and
   * stretch so limb offsets layer cleanly on top of the body motion.
   */
  private updateLimbs(deltaTime: number): void {
    const phase = this.locomotionPhase;
    const moveAmount = this.smoothedMoveAmount;
    const facing = this.facingDirection;

    // ── Legs — diagonal gait ──
    // Cat trot: FL + BR lift together, FR + BL lift together (180° apart).
    // We modulate by `moveAmount` so idle cats keep legs still. Grounded
    // gating avoids the "treadmill in mid-air" look during jumps.
    if (this.isGrounded && moveAmount > 0.02) {
      const lift = 0.06 * moveAmount;
      const swing = 0.08 * moveAmount;
      const phaseA = Math.sin(phase);
      const phaseB = Math.sin(phase + Math.PI);
      const liftA = Math.max(0, phaseA);
      const liftB = Math.max(0, phaseB);
      this.applyLegPose(this.limbLegFL, liftA * lift, phaseA * swing * facing);
      this.applyLegPose(this.limbLegBR, liftA * lift, phaseA * swing * facing);
      this.applyLegPose(this.limbLegFR, liftB * lift, phaseB * swing * facing);
      this.applyLegPose(this.limbLegBL, liftB * lift, phaseB * swing * facing);
    } else {
      // Idle: legs settle back to base with a tiny breath.
      const idle = Math.sin(phase * 0.3) * 0.005;
      this.applyLegPose(this.limbLegFL, idle, 0);
      this.applyLegPose(this.limbLegBR, idle, 0);
      this.applyLegPose(this.limbLegFR, idle, 0);
      this.applyLegPose(this.limbLegBL, idle, 0);
    }

    // ── Tail sway ──
    // Lazy S-curve when idle, more vigorous wag when the cat is moving.
    // The tail rotates around Z and Y to read as a full counter-balance.
    if (this.limbTail) {
      const base = this.limbBaseRotations.get(this.limbTail);
      if (base) {
        const sway = Math.sin(phase * 0.7) * (0.18 + moveAmount * 0.45);
        const wag = Math.cos(phase * 1.2) * (0.05 + moveAmount * 0.18);
        this.limbTail.rotation.set(
          base.x + wag,
          base.y + sway * 0.35,
          base.z + sway,
        );
      }
    }

    // ── Head bob ──
    // Slight counter-bob opposite the body bob — sells "looking forward
    // while the body moves beneath me" instead of the whole model swaying.
    if (this.limbHead) {
      const base = this.limbBasePositions.get(this.limbHead);
      const baseRot = this.limbBaseRotations.get(this.limbHead);
      if (base && baseRot) {
        const counterBob = -Math.sin(phase) * 0.02 * moveAmount;
        this.limbHead.position.set(base.x, base.y + counterBob, base.z);
        // Tiny forward-lean proportional to speed so she's peering into
        // where she's going.
        this.limbHead.rotation.set(
          baseRot.x + moveAmount * 0.08,
          baseRot.y,
          baseRot.z,
        );
      }
    }

    // ── Ear twitches ──
    // Random twitches every 1–3 seconds, scaled up slightly when idle
    // (cat "listening to the world" behaviour).
    this.earTwitchTimer -= deltaTime;
    if (this.earTwitchTimer <= 0) {
      this.earTwitchTimer = 1.2 + Math.random() * 1.8;
      this.earTwitchOffset = (Math.random() - 0.5) * 0.35;
    }
    const twitchDecay = Math.max(0, this.earTwitchOffset);
    this.earTwitchOffset *= Math.max(0, 1 - deltaTime * 6);
    const applyEar = (ear: TransformNode | null, side: number): void => {
      if (!ear) return;
      const base = this.limbBaseRotations.get(ear);
      if (!base) return;
      ear.rotation.set(
        base.x + twitchDecay * side,
        base.y,
        base.z + twitchDecay * 0.2 * side,
      );
    };
    applyEar(this.limbEarL, 1);
    applyEar(this.limbEarR, -1);
  }

  private applyLegPose(
    leg: TransformNode | null,
    yOffset: number,
    xOffset: number,
  ): void {
    if (!leg) return;
    const base = this.limbBasePositions.get(leg);
    if (!base) return;
    leg.position.set(base.x + xOffset, base.y + yOffset, base.z);
  }

  private updateProceduralPose(
    deltaTime: number,
    moveAxis: number,
    horizontalVelocity: number,
    verticalVelocity: number,
    landedThisFrame: boolean,
    jumpedThisFrame = false,
  ): void {
    const root = this.modelRoot;
    const basePosition = this.modelBasePosition;
    if (!root || !basePosition) {
      return;
    }

    if (moveAxis < -0.01) this.facingDirection = -1;
    else if (moveAxis > 0.01) this.facingDirection = 1;

    const moveAlpha = Math.min(1, deltaTime * 10);
    const normalizedMove = Math.min(1, Math.abs(horizontalVelocity) / PLAYER.MOVE_SPEED);
    this.smoothedMoveAmount += (normalizedMove - this.smoothedMoveAmount) * moveAlpha;

    if (this.isGrounded && this.smoothedMoveAmount > 0.02) {
      this.locomotionPhase += deltaTime * (6 + this.smoothedMoveAmount * 10);
    } else {
      this.locomotionPhase += deltaTime * 2;
    }

    if (landedThisFrame) {
      this.landingImpact = Math.min(
        1,
        Math.max(0.18, Math.abs(verticalVelocity) / Math.max(PLAYER.MAX_FALL_SPEED, 0.001)),
      );
    }
    this.landingImpact = Math.max(0, this.landingImpact - deltaTime * 4.5);

    const walkBob = this.isGrounded
      ? Math.sin(this.locomotionPhase) * 0.05 * this.smoothedMoveAmount
      : 0;
    const idleBreath = Math.sin(this.locomotionPhase * 0.5) * 0.01 * (1 - this.smoothedMoveAmount);
    const lean =
      this.isGrounded
        ? -Math.sign(horizontalVelocity || this.facingDirection) * 0.16 * this.smoothedMoveAmount
        : -Math.sign(horizontalVelocity || this.facingDirection) * 0.06;
    const sideSway = this.isGrounded
      ? Math.sin(this.locomotionPhase * 0.5) * 0.03 * this.smoothedMoveAmount
      : 0;

    // Decay the post-jump compress over ~140ms. While it's active the
    // character reads as squatted (wider, shorter) for the first frames
    // of ascent, then the standard jumpStretch takes over. Gives the
    // illusion of anticipation even though the jump impulse has
    // already fired — readable at 60fps, invisible on frame-by-frame.
    this.launchCompress = Math.max(0, this.launchCompress - deltaTime * 7);

    // Decay the denied-input rattle. Short and snappy — 250ms decay so
    // spamming the direction keys while anchored produces a continuous
    // micro-shudder rather than a single gentle wiggle.
    this.deniedInputRattle = Math.max(0, this.deniedInputRattle - deltaTime * 4);

    const jumpStretch = !this.isGrounded && verticalVelocity > 0
      ? Math.min(0.18, verticalVelocity * 0.015)
      : 0;
    const fallCompress = !this.isGrounded && verticalVelocity < 0
      ? Math.min(0.12, -verticalVelocity * 0.01)
      : 0;
    const landingSquash = this.landingImpact * 0.16;
    // Blend the launch compress into a squat while it's active; fades
    // out over the first ~140ms of jump ascent.
    const launchSquat = this.launchCompress * 0.22;

    const scaleX =
      this.modelBaseScale *
      (1 + landingSquash * 0.35 + launchSquat * 0.3 - jumpStretch * 0.28 + fallCompress * 0.18);
    const scaleY =
      this.modelBaseScale *
      (1 - landingSquash * 0.55 - launchSquat * 0.55 + jumpStretch * 0.9 - fallCompress * 0.22);
    const scaleZ =
      this.modelBaseScale *
      (1 + landingSquash * 0.18 + launchSquat * 0.18 - jumpStretch * 0.16 + fallCompress * 0.12);

    // High-frequency rattle when the player tries to walk while pinned.
    // Small amplitude so it reads as "pushing against the ground," not
    // a glitch. Uses locomotionPhase as the jitter seed so it syncs
    // with the existing per-frame phase.
    const rattleX = Math.sin(this.locomotionPhase * 60) * 0.04 * this.deniedInputRattle;
    const rattleY = Math.cos(this.locomotionPhase * 60) * 0.02 * this.deniedInputRattle;

    // Skeletal clips carry gait, breathing, anticipation and landing; the
    // root keeps facing, the anchor rattle and a gentler squash/stretch so
    // the two don't double up.
    const animator = this.animator;
    if (animator) {
      animator.update(
        {
          grounded: this.isGrounded,
          speed: Math.abs(horizontalVelocity),
          vy: verticalVelocity,
          jumped: jumpedThisFrame,
          landed: landedThisFrame,
        },
        deltaTime,
      );
      const soft = (k: number) => this.modelBaseScale * (1 + (k / this.modelBaseScale - 1) * 0.4);
      root.position.copyFrom(basePosition);
      root.position.x += rattleX;
      root.position.y += rattleY;
      this._poseRootPos.copyFrom(root.position);
      root.scaling.set(soft(scaleX), soft(scaleY), soft(scaleZ));
      root.rotation.y = this.facingDirection > 0 ? this.modelBaseRotationY : -this.modelBaseRotationY;
      root.rotation.z = lean * 0.3;
      this.applyAbilityPoseOverlay(deltaTime);
      return;
    }

    root.position.copyFrom(basePosition);
    root.position.x += sideSway * this.facingDirection + rattleX;
    root.position.y += walkBob + idleBreath - this.landingImpact * 0.06 + rattleY;
    this._poseRootPos.copyFrom(root.position);
    root.scaling.set(scaleX, scaleY, scaleZ);
    root.rotation.y = this.facingDirection > 0 ? this.modelBaseRotationY : -this.modelBaseRotationY;
    root.rotation.z = lean + Math.sin(this.locomotionPhase * 0.5) * 0.025 * this.smoothedMoveAmount;

    // Layer limb animation on top of the root-level squash/sway. The
    // legs move in a diagonal gait, tail sways, head bobs counter, ears
    // twitch. No-op if the model hasn't finished loading yet.
    this.updateLimbs(deltaTime);

    // Ability pose overlay hook — MUST stay the last step: the base pose
    // above uses overwrite semantics (position.copyFrom / scaling.set /
    // rotation.z assignment), so subclass overlays compose with += / *=
    // on top of the finished frame.
    this.applyAbilityPoseOverlay(deltaTime);
  }

  /**
   * Subclass hook for ability-specific pose overlays (e.g. Basil's
   * Turn-Table Touch "DJ scratch" lean, Q-032). Called as the LAST step
   * of `updateProceduralPose`, after the base pose + limb composition.
   * Implementations must only touch `modelRoot` / limb transform nodes —
   * never `this.mesh` (the physics capsule), `aggregate`, or
   * `isGrounded` — and must compose with `+=` / `*=` because the base
   * pose overwrites the root transform every frame. Default no-op.
   */
  protected applyAbilityPoseOverlay(_deltaTime: number): void {
    // no-op — subclasses layer ability-specific root-level motion
  }
}
